const FP_STORAGE_KEY = 'imagecat_fp';
const FP_RE = /^[a-f0-9]{64}$/;

let cachedFp: string | null = null;

function readFpStorage(): string | null {
  try {
    const value = localStorage.getItem(FP_STORAGE_KEY);
    if (value && FP_RE.test(value)) {
      return value;
    }
  } catch {
    // ignore
  }
  return null;
}

function writeFpStorage(value: string): void {
  try {
    localStorage.setItem(FP_STORAGE_KEY, value);
  } catch {
    // ignore
  }
}

function webglInfo(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl');
    if (!gl || !(gl instanceof WebGLRenderingContext)) {
      return 'none';
    }
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (ext) {
      return `${gl.getParameter(ext.UNMASKED_VENDOR_WEBGL)}|${gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)}`;
    }
    return `${gl.getParameter(gl.VENDOR)}|${gl.getParameter(gl.RENDERER)}`;
  } catch {
    return 'none';
  }
}

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(hash)].map(b => b.toString(16).padStart(2, '0')).join('');
}

/** Стабильные сигналы браузера — после первого вычисления кэшируются навсегда. */
async function computeStableFingerprint(): Promise<string> {
  const nav = navigator as Navigator & { deviceMemory?: number };
  const parts = [
    nav.language,
    nav.languages?.join(',') ?? '',
    nav.platform,
    nav.userAgent,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    String(nav.hardwareConcurrency ?? 0),
    String(nav.deviceMemory ?? 0),
    String(nav.maxTouchPoints ?? 0),
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    String(window.devicePixelRatio ?? 1),
    webglInfo(),
  ];
  return sha256Hex(parts.join('\n'));
}

/** Вычислить один раз и сохранить — hash больше не меняется на этом устройстве. */
export async function ensureFingerprint(): Promise<string> {
  if (cachedFp && FP_RE.test(cachedFp)) {
    return cachedFp;
  }
  const stored = readFpStorage();
  if (stored) {
    cachedFp = stored;
    return stored;
  }
  const fp = await computeStableFingerprint();
  cachedFp = fp;
  writeFpStorage(fp);
  return fp;
}

export function getFingerprint(): string | null {
  return cachedFp;
}
